import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext'; // Import AuthContext
import { Box, Paper, Typography, Button, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

const VerifyEmailPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { isAdmin } = useAuth(); // Get admin status
    const [status, setStatus] = useState('verifying'); 
    const [message, setMessage] = useState('');

    useEffect(() => {
        const verify = async () => {
            const oobCode = searchParams.get('oobCode');
            if (!oobCode) {
                setStatus('error');
                setMessage('Invalid verification link. No code provided.');
                return;
            }

            try {
                await applyActionCode(auth, oobCode);
                setStatus('success');
                if (auth.currentUser) {
                    await auth.currentUser.reload();
                }
            } catch (error) {
                console.error('Verification error:', error);
                setStatus('error');
                setMessage(error.message || 'Failed to verify email. The link may have expired.');
            }
        };

        verify();
    }, [searchParams]);

    const handleContinue = () => {
             // If not logged in, just go to login
            if (!auth.currentUser) {
            navigate('/login');
        return;
    }
        if (isAdmin) {
            // Send admins to the admin portal
            navigate('/admin');
        } else {
            // Send standard users directly to their account page to finish 2FA
            navigate('/client-dashboard/account');
        }
    };

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            bgcolor: 'grey.100',
            p: 2 
        }}>
            <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%', textAlign: 'center' }}>
                {status === 'verifying' && (
                    <>
                        <CircularProgress size={40} sx={{ mb: 2 }} />
                        <Typography variant="h6">Verifying your email...</Typography>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
                        <Typography variant="h5" gutterBottom>Email Verified!</Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            Your email is verified. You can now return to the app to enable Two-Factor Authentication.
                        </Typography>
                        <Button 
                            variant="contained" 
                            fullWidth 
                            onClick={handleContinue}
                        >
                            {isAdmin ? "Return to Admin Portal" : "Go to Account Settings"}
                        </Button>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
                        <Typography variant="h5" gutterBottom>Verification Failed</Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            {message}
                        </Typography>
                        <Button 
                            variant="outlined" 
                            fullWidth 
                            onClick={() => navigate('/login')}
                        >
                            Back to Login
                        </Button>
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default VerifyEmailPage;